<div class="boost-wrapper" [style.--boost-bg-image]="backgroundImage ? 'url(' + backgroundImage + ')' : 'none'">
  <div class="boost-grid" [class.boost-grid--public]="!isAuthenticated">

    <!-- LEFT COLUMN: Form -->
    <div class="boost-form-column">

      <!-- Header (only when not authenticated) -->
      @if (!isAuthenticated) {
        <div class="boost-header">
          <div class="header-left">
            <img [src]="brandLogo" alt="" class="brand-logo" />
            <img [src]="brandWordmark" [alt]="branding?.name || 'Beltrashow'" class="brand-wordmark" />
          </div>
          <div class="header-right">
            <app-button
              [label]="'boost.header.register' | transloco"
              variant="secondary"
              size="medium"
              (onClick)="goToRegister()">
            </app-button>
            <app-button
              [label]="'boost.header.login' | transloco"
              variant="primary"
              size="medium"
              (onClick)="goToLogin()">
            </app-button>
          </div>
        </div>
      }

      <!-- Title Section -->
      <div class="boost-title-section">
        <h1 class="boost-title">{{ isAuthenticated ? ('boost.title.authenticated' | transloco) : ('boost.title.public' | transloco) }}</h1>
        <p class="subtitle">
          {{ isAuthenticated
            ? ('boost.subtitle.authenticated' | transloco)
            : ('boost.subtitle.public' | transloco)
          }}
        </p>
      </div>

      <!-- Form -->
      <form [formGroup]="boostForm" (ngSubmit)="onSubmit()" class="boost-form">
        <!-- Fields: a white card on mobile, transparent (display: contents) on desktop -->
        <div class="boost-fields">

        <!-- Platform Pills and Service Selection -->
        <app-product-picker
          [texts]="{
            platform: 'boost.form.platform' | transloco,
            loadPlatformsError: 'boost.form.loadPlatformsError' | transloco,
            noPlatforms: 'boost.form.noPlatforms' | transloco,
            service: 'boost.form.service' | transloco,
            loadProductsError: 'boost.form.loadProductsError' | transloco,
            noProducts: 'boost.form.noProducts' | transloco,
            searchService: 'boost.form.searchService' | transloco,
            selectService: 'boost.form.selectService' | transloco
          }">
        </app-product-picker>

        <!-- Link and Quantity Row -->
        <div class="locked-row">
          <!-- Link Field -->
          <div class="form-group">
            <label class="form-label">
              <span>{{ 'boost.form.link' | transloco }}</span>
              @if (!isAuthenticated) {
                <lucide-icon name="Lock" class="icon-em lock-icon"></lucide-icon>
              }
            </label>
            @if (!isAuthenticated) {
              <div class="locked-input">
                <lucide-icon name="Lock" class="icon-em"></lucide-icon>
                <span>{{ 'boost.form.registerToContinue' | transloco }}</span>
              </div>
            } @else {
              <app-input
                formControlName="link"
                type="text"
                size="medium"
                [placeholder]="linkPlaceholder"
                [error]="isFieldInvalid('link')"
                [errorMessage]="getFieldError('link')">
              </app-input>
            }
          </div>

          <!-- Quantity Field -->
          <div class="form-group">
            <label class="form-label">
              <span>{{ 'boost.form.quantity' | transloco }}</span>
              @if (!isAuthenticated) {
                <lucide-icon name="Lock" class="icon-em lock-icon"></lucide-icon>
              }
            </label>
              @if (!isAuthenticated) {
                <div class="locked-input">
                  <lucide-icon name="Lock" class="icon-em"></lucide-icon>
                  <span>---</span>
                </div>
              } @else if (requiresCustomComments) {
                <app-button
                  [label]="'boost.form.addComments' | transloco:{ count: getCommentsCount() }"
                  variant="secondary"
                  size="medium"
                  (onClick)="openCommentsDialog()">
                </app-button>
              } @else {
                <app-input
                  formControlName="quantity"
                  type="number"
                  size="medium"
                  [placeholder]="selectedProductDetails ? ('boost.form.quantityRange' | transloco:{ min: (minAmount | appNumber), max: maxAmount ? (maxAmount | appNumber) : '∞' }) : ('boost.form.quantityPlaceholder' | transloco)"
                  [loading]="catalog.loadingProductDetails"
                  [error]="isFieldInvalid('quantity')"
                  [errorMessage]="getFieldError('quantity')">
                </app-input>
              }
          </div>
        </div>

        </div>

        <!-- CTA and Price Row -->
        <div class="cta-row">
          <app-button
            [label]="submitButtonLabel"
            variant="primary"
            size="medium"
            type="submit"
            [disabled]="checkout.creatingOrder"
            class="submit-button">
          </app-button>

          <div class="price-area">
            <span class="price-label">{{ 'boost.form.total' | transloco }}</span>
            @if (isAuthenticated && pricing.price > 0) {
              @if (pricing.volumeOriginalPrice > 0) {
                <span class="price-original">{{ pricing.volumeOriginalPrice | appCurrency }}</span>
                <span class="price-value has-discount">{{ pricing.price | appCurrency }}</span>
              } @else {
                <span class="price-value">{{ pricing.price | appCurrency }}</span>
              }
            } @else if (isAuthenticated && pricing.priceError) {
              <span class="price-value" role="alert">{{ 'common.states.priceError' | transloco }}</span>
              <app-button [label]="'common.actions.retry' | transloco" variant="secondary" size="small" (onClick)="retryPrice()"></app-button>
            } @else {
              <span class="price-value">-- €</span>
            }
          </div>
        </div>

      </form>
    </div>

    <!-- RIGHT COLUMN: Summary or Sign Up Pitch -->
    <div class="boost-summary-column">
      <!-- Panel for authenticated users: order summary -->
      @if (isAuthenticated) {
        @if (isProductSelected && selectedProductDetails) {
          <div class="order-summary-panel">

            <div class="osp-header">
              <div class="osp-icon">
                <lucide-icon name="Receipt" class="icon-em"></lucide-icon>
              </div>
              <div class="osp-header-text">
                <span class="osp-title">{{ 'boost.summary.title' | transloco }}</span>
                <span class="osp-subtitle">{{ 'boost.summary.subtitle' | transloco }}</span>
              </div>
            </div>

            <div class="osp-lines">
              <div class="osp-line">
                <span class="osp-line-label">{{ 'boost.summary.platform' | transloco }}</span>
                <span class="osp-line-value">{{ platformName }}</span>
              </div>
              <div class="osp-line">
                <span class="osp-line-label">{{ 'boost.summary.service' | transloco }}</span>
                <span class="osp-line-value">{{ productName }}</span>
              </div>
              <div class="osp-line">
                <span class="osp-line-label">{{ 'boost.summary.quantity' | transloco }}</span>
                <!-- No quantity yet (e.g. after an order the form resets): '--' like the total -->
                <span class="osp-line-value">{{ quantity > 0 ? (quantity | appNumber) : '--' }}</span>
              </div>
            </div>

            <div class="osp-promo-code">
              @if (!pricing.promoCodeApplied) {
                <div class="promo-input-row">
                  <input
                    #promoInput
                    type="text"
                    [placeholder]="'boost.summary.promoPlaceholder' | transloco"
                    class="promo-input"
                    (keydown.enter)="applyPromoCode(promoInput.value)"
                  />
                  <button class="promo-apply-btn" (click)="applyPromoCode(promoInput.value)" [disabled]="!promoInput.value">
                    {{ 'boost.summary.apply' | transloco }}
                  </button>
                </div>
              } @else {
                <div class="promo-applied-row">
                  <span class="promo-applied-label">
                    <lucide-icon name="Tag" [size]="14"></lucide-icon>
                    {{ pricing.promoCode }}
                  </span>
                  <button type="button" class="promo-remove-btn" (click)="removePromoCode()" [attr.aria-label]="'boost.summary.removePromo' | transloco">
                    <lucide-icon name="X" [size]="14"></lucide-icon>
                  </button>
                </div>
              }
            </div>

            @if (pricing.discount) {
              <div class="osp-discount-section">
                <div class="osp-discount-line">
                  <span class="osp-discount-label">{{ 'boost.summary.subtotal' | transloco }}</span>
                  <span class="osp-subtotal-value">{{ pricing.discount.originalPrice | appCurrency }}</span>
                </div>
                <div class="osp-discount-line">
                  <span class="osp-discount-name">{{ pricing.discount.discountName || ('boost.summary.discount' | transloco) }}{{ pricing.discount.discountType === 'PERCENTAGE' ? ' (-' + pricing.discount.discountValue + '%)' : '' }}</span>
                  <span class="osp-discount-amount">-{{ pricing.discount.discountAmount | appCurrency }}</span>
                </div>
              </div>
            }

            <div class="osp-total">
              <span class="osp-total-label">{{ 'boost.summary.total' | transloco }}</span>
              @if (pricing.discount || pricing.price > 0) {
                <span class="osp-total-amount">{{ (pricing.discount ? pricing.discount.finalPrice : pricing.price) | appCurrency }}</span>
              } @else {
                <span class="osp-total-amount">-- €</span>
              }
            </div>

          </div>
        } @else {
          <div class="empty-summary">
            <div class="empty-icon">
              <lucide-icon name="ShoppingCart" class="icon-em"></lucide-icon>
            </div>
            <h3 class="empty-title">{{ 'boost.empty.title' | transloco }}</h3>
            <p class="empty-subtitle">{{ 'boost.empty.subtitle' | transloco }}</p>
          </div>
        }
      }

      <!-- Panel for NON-authenticated users: sign-up pitch -->
      @if (!isAuthenticated) {
        <div class="signup-pitch-panel">
          <div class="pitch-card">

            <div class="pitch-icon">
              <lucide-icon name="Lock" class="icon-em"></lucide-icon>
            </div>

            <h3 class="pitch-title">{{ 'boost.pitch.title' | transloco }}</h3>

            <p class="pitch-subtitle">{{ 'boost.pitch.subtitle' | transloco }}</p>

            <div class="benefits-list">
              <div class="benefit-item">
                <lucide-icon name="Eye" class="icon-em"></lucide-icon>
                <span>{{ 'boost.pitch.benefit1' | transloco }}</span>
              </div>
              <div class="benefit-item">
                <lucide-icon name="Zap" class="icon-em"></lucide-icon>
                <span>{{ 'boost.pitch.benefit2' | transloco }}</span>
              </div>
              <div class="benefit-item">
                <lucide-icon name="History" class="icon-em"></lucide-icon>
                <span>{{ 'boost.pitch.benefit3' | transloco }}</span>
              </div>
              <div class="benefit-item">
                <lucide-icon name="Headphones" class="icon-em"></lucide-icon>
                <span>{{ 'boost.pitch.benefit4' | transloco }}</span>
              </div>
            </div>

            <app-button
              [label]="'boost.pitch.cta' | transloco"
              variant="primary"
              size="medium"
              (onClick)="goToRegister()"
              class="pitch-cta">
            </app-button>
          </div>
        </div>
      }
    </div>

  </div>

  <!-- Dialogs -->
  @if (isAuthenticated) {
    <p-dialog
      [(visible)]="showCommentsDialog"
      [modal]="true"
      [closable]="true"
      [appendTo]="'body'"
      [styleClass]="'comments-dialog'"
      [draggable]="false"
      [resizable]="false">
      <ng-template pTemplate="header">
        <div class="dialog-header" [attr.data-subtitle]="'boost.comments.dialogSubtitle' | transloco">
          <span class="dialog-title">{{ 'boost.comments.dialogTitle' | transloco }}</span>
        </div>
      </ng-template>

      <div class="dialog-body">
        <p class="dialog-description">
          {{ 'boost.comments.description' | transloco:{ count: getCommentsCount() } }}
        </p>
        <label class="textarea-label">{{ 'boost.comments.label' | transloco }}</label>
        <textarea
          class="comments-textarea"
          [(ngModel)]="customComments"
          (ngModelChange)="onCommentsChange($event)"
          [rows]="12"
          [placeholder]="'boost.comments.placeholder' | transloco">
        </textarea>
      </div>

      <div class="dialog-actions">
        <app-button
          [label]="'common.actions.cancel' | transloco"
          variant="ghost"
          size="medium"
          (onClick)="showCommentsDialog = false">
        </app-button>
        <app-button
          [label]="'common.actions.save' | transloco"
          size="medium"
          (onClick)="saveComments()">
        </app-button>
      </div>
    </p-dialog>

    <!-- Payment method modal -->
    <app-payment-method-modal
      #paymentModal
      [(visible)]="showPaymentModal"
      [totalAmount]="calculatePrice()"
      [originalAmount]="pricing.discount ? pricing.discount.originalPrice : 0"
      [discountName]="pricing.discount?.discountName || ''"
      [productName]="selectedProductName"
      [quantity]="requiresCustomComments ? getCommentsCount() : boostForm.get('quantity')?.value"
      [orderId]="checkout.pendingOrderId"
      [isCreatingOrder]="checkout.isCreatingManualOrder"
      (paymentMethodSelected)="onPaymentMethodSelected($event)"
      (manualPaymentSelected)="onManualPaymentSelected($event)"
      (manualPaymentConfirmed)="onManualPaymentConfirmed($event)"
      (cryptoPaymentSelected)="onCryptoPaymentSelected($event)"
      (cancelled)="onPaymentModalCancelled()">
    </app-payment-method-modal>
  }
</div>
